// src/components/DetailModal.js
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import StatusBadge from './StatusBadge';
import { ClipboardListIcon, UserIcon, MegaphoneIcon, AlertTriangleIcon } from './Icon';
import { api } from '../utils/api';
import { can } from '../utils/permissions';
import { getCurrentUser } from '../utils/auth';

const STATUSES = ['Pending', 'Reviewed', 'Approved', 'Rejected'];

const CONFIRM_META = {
  Approved: { verb: 'approve',             btn: 'Yes, Approve',  color: '#27ae60' },
  Rejected: { verb: 'reject',              btn: 'Yes, Reject',   color: '#c0392b' },
  Reviewed: { verb: 'mark as reviewed',    btn: 'Yes, Review',   color: '#2980b9' },
  Pending:  { verb: 'set back to pending', btn: 'Yes, Continue', color: '#f39c12' },
};

const Row = ({ label, val, span }) => (
  <div className={`dm-row${span ? ' dm-row--span' : ''}`}>
    <div className="dm-row-label">{label}</div>
    <div className="dm-row-val">{val || <span className="dm-dash">—</span>}</div>
  </div>
);
Row.propTypes = { label: PropTypes.string.isRequired, val: PropTypes.node, span: PropTypes.bool };
Row.defaultProps = { val: null, span: false };

const Section = ({ icon, title }) => (
  <div className="dm-section">
    {icon && <span className="dm-section-icon">{icon}</span>}
    <span className="dm-section-text">{title}</span>
  </div>
);
Section.propTypes = { icon: PropTypes.node, title: PropTypes.string.isRequired };
Section.defaultProps = { icon: null };

export default function DetailModal({ id, onClose, onStatusChange }) {
  const canStatus = can(getCurrentUser(), 'submissions.status');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(null);

  useEffect(() => {
    api.detail(id).then(d => { setData(d); setLoading(false); });
  }, [id]);

  const requestStatus = (status) => setConfirmStatus(status);

  const confirmUpdate = async () => {
    const status = confirmStatus;
    setConfirmStatus(null);
    setUpdating(true);
    await api.updateStatus(id, status);
    setData(d => ({ ...d, status }));
    setUpdating(false);
    onStatusChange?.(id, status);
  };

  const handleOverlayKey = (e) => { if (e.key === 'Escape') onClose(); };
  const handleConfirmKey = (e) => { if (e.key === 'Escape') setConfirmStatus(null); };

  const renderBody = () => {
    if (data?._encrypted) {
      return (
        <div className="dm-enc-notice">
          <div className="dm-enc-icon">🔒</div>
          <div className="dm-enc-title">Data is Encrypted</div>
          <div className="dm-enc-text">
            {data._keyError
              ? `Decryption failed: ${data._keyError}`
              : 'Set your encryption key in Settings to view this record.'}
          </div>
        </div>
      );
    }

    return (
      <div className="dm-body">
        {canStatus && (
          <div className="dm-status-row">
            <span className="dm-status-label">Update Status:</span>
            {STATUSES.map(st => (
              <button
                key={st}
                disabled={updating || data.status === st}
                onClick={() => requestStatus(st)}
                className={`dm-status-btn${data.status === st ? ' is-current' : ''}`}
              >
                {st}
              </button>
            ))}
          </div>
        )}

        <Section icon={<ClipboardListIcon size={15} />} title="Project Details" />
        <div className="dm-grid">
          <Row label="Code Name"   val={data.code_name} />
          <Row label="Location"    val={data.location} />
          <Row label="BHK Config"  val={data.bhk_config} />
          <Row label="Price Range" val={data.price_range} />
          <Row label="Unit Area"   val={data.unit_area ? `${data.unit_area} sq.ft.` : null} />
          <Row label="Pref. Floor" val={data.pref_floor} />
          <Row label="Facing"      val={data.facing} />
          <Row label="Cheque No."  val={data.cheque_no} />
          <Row label="Bank Name"   val={data.bank_name} />
          <Row label="Cheque Date" val={data.cheque_date} />
        </div>

        <Section icon={<UserIcon size={15} />} title="Applicant 1 (Primary)" />
        <div className="dm-grid">
          <Row label="Full Name"   val={data.name1} />
          <Row label="PAN"         val={data.pan1} />
          <Row label="Aadhaar"     val={data.aadhar1} />
          <Row label="Email"       val={data.email1} />
          <Row label="Mobile"      val={data.mobile1} />
          <Row label="Profession"  val={data.profession1} />
          <Row label="Company"     val={data.company1} />
          <Row label="Designation" val={data.designation1} />
          <Row label="Address"     val={data.address1} span />
        </div>

        {data.name2 && <>
          <Section icon={<UserIcon size={15} />} title="Applicant 2 (Co-applicant)" />
          <div className="dm-grid">
            <Row label="Full Name"   val={data.name2} />
            <Row label="PAN"         val={data.pan2} />
            <Row label="Aadhaar"     val={data.aadhar2} />
            <Row label="Email"       val={data.email2} />
            <Row label="Mobile"      val={data.mobile2} />
            <Row label="Profession"  val={data.profession2} />
            <Row label="Company"     val={data.company2} />
            <Row label="Designation" val={data.designation2} />
            {data.address2 && <Row label="Address" val={data.address2} span />}
          </div>
        </>}

        <Section icon={<MegaphoneIcon size={15} />} title="Source & Officials" />
        <div className="dm-grid">
          <Row label="Source Type"      val={data.source_type} />
          <Row label="Source Name"      val={data.source_name} />
          <Row label="Source Contact"   val={data.source_contact} />
          <Row label="GPP Employee"     val={data.gpp_emp_name} />
          <Row label="GPP Emp. Mobile"  val={data.gpp_emp_mobile} />
          <Row label="Submit Date"      val={data.submit_date} />
          <Row label="Submit Place"     val={data.submit_place} />
          <Row label="Submitted By"     val={data.submitted_by} />
          <Row label="Verified By"      val={data.verified_by} />
          <Row label="Approved By"      val={data.approved_by} />
          <Row label="Sourcing Mgr"     val={data.sourcing_manager} />
          <Row label="Sales Mgr"        val={data.sales_manager} />
          <Row label="Referral Partner" val={data.referral_partner} />
          {data.remark && <Row label="Remark" val={data.remark} span />}
        </div>
      </div>
    );
  };

  return (
    <div
      className="dm-overlay"
      role="presentation"
      onClick={e => e.target === e.currentTarget && onClose()}
      onKeyDown={handleOverlayKey}
    >
      <div className="dm-modal">
        <div className="dm-header">
          <div>
            <div className="dm-refno">{data?.ref_number || '...'}</div>
            <div className="dm-submitted">Submitted: {data?.created_at?.split(' ')[0]}</div>
          </div>
          <div className="dm-header-right">
            {data && !data._encrypted && <StatusBadge status={data.status} size="lg" />}
            <button className="dm-close" onClick={onClose}>✕</button>
          </div>
        </div>

        {loading ? <div className="loader-wrap"><div className="spinner" /></div> : renderBody()}

        {confirmStatus && (
          <div
            className="dm-confirm-overlay"
            role="presentation"
            onClick={e => e.target === e.currentTarget && setConfirmStatus(null)}
            onKeyDown={handleConfirmKey}
          >
            <div className="dm-confirm">
              <div className="dm-confirm-icon" style={{ color: CONFIRM_META[confirmStatus]?.color }}>
                <AlertTriangleIcon size={38} />
              </div>
              <div className="dm-confirm-title">Confirm Status Change</div>
              <div className="dm-confirm-text">
                Are you sure you want to{' '}
                <strong>{CONFIRM_META[confirmStatus]?.verb ?? `set as ${confirmStatus}`}</strong>{' '}
                this application?
              </div>
              <div className="dm-confirm-actions">
                <button className="dm-confirm-cancel" onClick={() => setConfirmStatus(null)} disabled={updating}>
                  Cancel
                </button>
                <button
                  className="dm-confirm-ok"
                  style={{ background: CONFIRM_META[confirmStatus]?.color }}
                  onClick={confirmUpdate}
                  disabled={updating}
                >
                  {CONFIRM_META[confirmStatus]?.btn ?? 'Yes, Continue'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

DetailModal.propTypes = {
  id: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func,
};
DetailModal.defaultProps = { onStatusChange: null };
